import React from "react";
import { Link } from "react-router-dom";
import { FiAlertTriangle, FiCheck, FiCheckCircle, FiRefreshCw, FiX } from "react-icons/fi";
import Button from "components/atoms/Button";
import FindingsList from "pages/StandardTreatment/shared/FindingsList";
import { ErrorState } from "pages/StandardTreatment/shared/ui";
import { VALIDATION_CHECKS } from "../constants";

// Tampilan response GET /version-rule/{id}/validation (ValidationResult):
// valid, checks, finding_count, findings, counts. Findings dari backend, bukan dihitung UI.
const ValidationStep = ({ versionId, validation, onRevalidate, onGoToStep }) => {
  const { data, loading, error } = validation;
  const failed = new Set((data?.findings || []).map((f) => f.check));

  const fixAction = (finding) => {
    const fix = VALIDATION_CHECKS[finding.check]?.fix;
    const stepLink = (s, label) => (
      <button key={s} type="button" className="mps-link" onClick={() => onGoToStep(s)}>
        {label}
      </button>
    );
    if (fix === "step2and3")
      return <span className="mps-row-actions">{[stepLink(2, "Kondisi"), stepLink(3, "Terapi")]}</span>;
    if (fix === "step3") return stepLink(3, "Perbaiki terapi");
    if (fix === "masterObat") return <Link className="mps-link" to="/master-obat">Master Obat</Link>;
    if (fix === "masterDoseBand") return <Link className="mps-link" to="/dosis-standar">Dosis Standar</Link>;
    return <Link className="mps-link" to={`/standard-treatment/rules?version_id=${versionId}`}>Daftar Rules</Link>;
  };

  return (
    <>
      <div className="mps-result-head mb-3">
        <p className="mps-muted mb-0">
          Pemeriksaan kelengkapan yang sama dengan saat rilis. Perbaiki findings, lalu validasi ulang.
        </p>
        <Button type="button" variant="secondary" size="normal" onClick={onRevalidate} isDisabled={loading}>
          <FiRefreshCw size={16} className="mr-2" />
          {loading ? "Memvalidasi..." : "Validasi ulang"}
        </Button>
      </div>

      {error && <ErrorState title="Validasi gagal dijalankan" messages={error} onRetry={onRevalidate} />}
      {loading && !data && <p className="mps-muted">Menjalankan validasi...</p>}

      {data && (
        <>
          <div className={`mps-alert ${data.valid ? "is-success" : "is-warning"}`} role="status">
            {data.valid ? <FiCheckCircle size={20} className="mps-alert-icon" /> : <FiAlertTriangle size={20} className="mps-alert-icon" />}
            <div>
              <p className="mps-alert-title">
                {data.valid ? `Versi ${data.version} valid` : `Versi ${data.version} belum valid — ${data.finding_count} findings`}
              </p>
              <p className="mps-alert-text">
                {data.valid
                  ? "Seluruh pemeriksaan lulus. Versi siap disimulasikan dan dirilis."
                  : "Rilis akan ditolak selama masih ada findings."}{" "}
                {data.counts.rule_count} rule · {data.counts.condition_count} kondisi · {data.counts.therapy_count} terapi.
              </p>
            </div>
          </div>

          <div className="row">
            <div className="col-lg-5 mps-field">
              <section className="mps-subcard h-100">
                <h3 className="mps-section-title mt-0">Pemeriksaan ({data.checks.length})</h3>
                <ul className="mps-checklist">
                  {data.checks.map((check) => {
                    const passed = !failed.has(check);
                    return (
                      <li key={check} className={passed ? "is-pass" : "is-fail"} title={check}>
                        {passed ? <FiCheck size={16} /> : <FiX size={16} />}
                        <span>{VALIDATION_CHECKS[check]?.label || check}</span>
                      </li>
                    );
                  })}
                </ul>
              </section>
            </div>
            <div className="col-lg-7 mps-field">
              <section className="mps-subcard h-100">
                <h3 className="mps-section-title mt-0">
                  Findings <span className="mps-muted">({data.finding_count})</span>
                </h3>
                <FindingsList findings={data.findings} renderAction={fixAction} />
              </section>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default ValidationStep;
